import { Link } from 'react-router-dom';
import { ActionIcon, Box, Container, Divider, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconBrandGithub, IconBrandX, IconBrandYoutube } from '@tabler/icons-react';

import { Logo } from '@/components/brand/Logo';
import classes from './Footer.module.css';

interface FooterLink {
  label: string;
  to: string;
  external?: boolean;
}

interface FooterGroup {
  title: string;
  links: FooterLink[];
}

const GROUPS: FooterGroup[] = [
  {
    title: 'Product',
    links: [
      { label: 'Download', to: '/download' },
      { label: 'Pricing', to: '/pricing' },
      { label: 'Analyzer', to: '/analyzer' },
      { label: 'Community', to: '/community' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'clancytuning.com', to: 'https://clancytuning.com', external: true },
      { label: 'About', to: '/about' },
      { label: 'Blog', to: '/blog' },
      { label: 'GitHub', to: 'https://github.com/horriblekimhodges-cmd/ClancyTuning', external: true },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy' },
      { label: 'Terms of Service', to: '/terms' },
    ],
  },
];

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box component="footer" role="contentinfo" className={classes.footer}>
      <Container size="xl" py={48}>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
          <Stack gap="sm">
            <Logo />
            <Text size="sm" c="var(--tf-text-secondary)" maw={280}>
              AI-assisted ECU tuning with deterministic safety. Built for tuners who care about
              getting it right.
            </Text>
            <Group gap="xs" mt="sm">
              <ActionIcon
                component="a"
                href="https://github.com/horriblekimhodges-cmd/ClancyTuning"
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                size="lg"
                aria-label="Clancy Tuning on GitHub"
              >
                <IconBrandGithub size={18} />
              </ActionIcon>
              <ActionIcon
                component="a"
                href="https://x.com/clancytuning"
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                size="lg"
                aria-label="Clancy Tuning on X"
              >
                <IconBrandX size={18} />
              </ActionIcon>
              <ActionIcon
                component="a"
                href="https://youtube.com/@clancytuning"
                target="_blank"
                rel="noopener noreferrer"
                variant="default"
                size="lg"
                aria-label="Clancy Tuning on YouTube"
              >
                <IconBrandYoutube size={18} />
              </ActionIcon>
            </Group>
          </Stack>

          {GROUPS.map((group) => (
            <Stack key={group.title} gap="sm">
              <Text fw={700} size="sm" tt="uppercase" c="var(--tf-text-primary)" lts="0.06em">
                {group.title}
              </Text>
              <Stack gap={6}>
                {group.links.map((link) =>
                  link.external ? (
                    <a
                      key={link.label}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.link}
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link key={link.label} to={link.to} className={classes.link}>
                      {link.label}
                    </Link>
                  ),
                )}
              </Stack>
            </Stack>
          ))}
        </SimpleGrid>

        <Divider my="xl" color="var(--tf-border)" />

        <Group justify="space-between" wrap="wrap" gap="sm">
          <Text size="xs" c="var(--tf-text-muted)">
            © {year} Clancy Tuning. All rights reserved.
          </Text>
          <Text size="xs" c="var(--tf-text-muted)">
            Clancy Tuning is a tool. Tuning your vehicle may void warranties or violate emissions
            regulations in your jurisdiction. You are responsible for what you flash.
          </Text>
        </Group>
      </Container>
    </Box>
  );
}
